import {useCallback,useEffect, useRef} from 'react'

export interface ScreenRecordingDetectionOptions {
  roomId: string
  roomName: string
  hasRecordingFlair: boolean
  userDid: string
  userHandle: string
  onRecordingDetected: (method: string) => void
  enabled?: boolean
}

/**
 * Hook to detect screen recording attempts in video call rooms
 * Only monitors if the room does NOT have the "Recording" flair
 */
export function useScreenRecordingDetection({
  roomId,
  roomName,
  hasRecordingFlair,
  userDid,
  userHandle,
  onRecordingDetected,
  enabled = true,
}: ScreenRecordingDetectionOptions) {
  const isMonitoring = useRef(false)
  const detectionTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasDetectedRef = useRef(false)

  const detectScreenCapture = useCallback(() => {
    if (hasDetectedRef.current) return
    hasDetectedRef.current = true
    onRecordingDetected('display_media')
  }, [onRecordingDetected])

  const detectVisibilityChange = useCallback(() => {
    // This can indicate screen sharing apps or recording software
    if (document.hidden && !hasDetectedRef.current) {
      // Secondary check - might be a false positive
      // Only trigger after a delay to avoid false positives from normal tab switching
      if (detectionTimerRef.current) {
        clearTimeout(detectionTimerRef.current)
      }

      detectionTimerRef.current = setTimeout(() => {
        // Check if there are any MediaStream tracks active
        if (typeof navigator !== 'undefined' && navigator.mediaDevices) {
          navigator.mediaDevices
            .enumerateDevices()
            .then(devices => {
              // If we have screen capture devices active, this might be recording
              const hasDisplay = devices.some(device =>
                device.label.toLowerCase().includes('screen'),
              )
              if (hasDisplay && !hasDetectedRef.current) {
                hasDetectedRef.current = true
                onRecordingDetected('visibility_api')
              }
            })
            .catch(err => {
              console.log('Device enumeration failed:', err)
            })
        }
      }, 3000) // 3 second delay to avoid false positives
    }
  }, [onRecordingDetected])

  const checkForDevTools = useCallback(() => {
    // Detect if DevTools is open (secondary indicator)
    const threshold = 160
    if (
      window.outerWidth - window.innerWidth > threshold ||
      window.outerHeight - window.innerHeight > threshold
    ) {
      // DevTools might be open - this is a weak signal
      // Don't trigger on this alone, just log
      console.log('DevTools detection: possible recording setup')
    }
  }, [])

  useEffect(() => {
    // Skip all monitoring if:
    // 1. Hook is disabled
    // 2. Room has Recording flair
    // 3. We're not in a browser environment
    if (!enabled || hasRecordingFlair || typeof window === 'undefined') {
      isMonitoring.current = false
      return
    }

    console.log(
      `🔍 Screen recording detection enabled for room: ${roomName} (ID: ${roomId})`,
    )
    console.log(`👤 Monitoring user: ${userHandle} (${userDid})`)
    isMonitoring.current = true
    hasDetectedRef.current = false

    // Method 1: Monitor for getDisplayMedia calls
    // This is the most reliable method for detecting screen capture
    // Only override once and check isMonitoring.current before triggering
    if (
      typeof navigator !== 'undefined' &&
      navigator.mediaDevices &&
      navigator.mediaDevices.getDisplayMedia &&
      !(window as any).__originalGetDisplayMedia
    ) {
      const originalGetDisplayMedia =
        navigator.mediaDevices.getDisplayMedia.bind(navigator.mediaDevices)

      // Store original globally so we only override once
      ;(window as any).__originalGetDisplayMedia = originalGetDisplayMedia

      // Override getDisplayMedia to detect when it's called
      navigator.mediaDevices.getDisplayMedia = function (
        ...args: Parameters<typeof originalGetDisplayMedia>
      ) {
        // Only trigger detection if actively monitoring (in a room call without recording tag)
        if (isMonitoring.current && !hasDetectedRef.current) {
          console.log('⚠️ Screen capture detected via getDisplayMedia')
          detectScreenCapture()
        }
        return originalGetDisplayMedia(...args)
      }
    }

    // Method 2: Monitor visibility changes
    document.addEventListener('visibilitychange', detectVisibilityChange)

    // Method 3: Periodic DevTools check (weak signal)
    const devToolsInterval = setInterval(checkForDevTools, 5000)

    // Cleanup
    return () => {
      isMonitoring.current = false
      console.log(
        `🔍 Screen recording detection disabled for room: ${roomName}`,
      )

      // Note: We don't restore getDisplayMedia here because the override
      // checks isMonitoring.current before triggering. This prevents issues
      // with the override persisting after leaving a room.

      document.removeEventListener('visibilitychange', detectVisibilityChange)
      clearInterval(devToolsInterval)

      if (detectionTimerRef.current) {
        clearTimeout(detectionTimerRef.current)
      }
    }
  }, [
    enabled,
    hasRecordingFlair,
    roomId,
    roomName,
    userDid,
    userHandle,
    detectScreenCapture,
    detectVisibilityChange,
    checkForDevTools,
  ])

  return {
    isMonitoring: isMonitoring.current,
  }
}
